<script lang="ts">
import { ref } from 'vue';

/**
 * Tooltip-suppression state, deliberately at MODULE scope so it survives the
 * remount that happens when a nav click navigates to another page: each page
 * (MatterPage, NarrativesPage) renders its OWN MatterNavRail instance, so a
 * per-instance ref would reset to `false` on the freshly-mounted rail and the
 * tooltip would pop straight back up under the stationary cursor. A shared
 * singleton set at click time is still suppressed when the new instance reads
 * it on mount, and clears on the first real pointer move.
 */
const suppressTooltip = ref(false);
let suppressAt: { x: number; y: number } | null = null;
</script>

<script setup lang="ts">
/**
 * Far-left vertical icon rail for the matter view (see
 * docs_for_claude/matter_context/figma-designs/latest/matter-navigation.png).
 * Always visible while a matter is open. Dialogues and Narratives are built
 * pages — clicking navigates to that section's landing page (from any of its
 * sub-routes) and expands its sidebar if collapsed (via requestSidebarExpand);
 * the item matching the current route shows the active indicator. Export is a
 * disabled placeholder until that page exists. Collapsing a sub-nav turns that
 * panel into its own vertical rail (which re-opens on click).
 *
 * Each item has a styled hover/focus tooltip (title + description).
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from '../../stores/matterStore';
import { requestSidebarExpand } from '../../composables/useResizableSidebar';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const matterStore = useMatterStore();

interface NavItem {
    key: string;
    icon: string;
    /** Use the outlined Material icon variant instead of the filled one. */
    outlined?: boolean;
    /** i18n key suffix under `matters.nav.*` for the title + description. */
    labelKey: string;
    /** Route this item navigates to (omit for not-yet-built placeholders). */
    routeName?: string;
    /** Other route names that should also light this item as active. */
    activeRouteNames?: string[];
    disabled?: boolean;
}

const navItems: NavItem[] = [
    {
        key: 'dialogues',
        icon: 'forum',
        labelKey: 'dialogues',
        routeName: 'matter',
        activeRouteNames: ['matter', 'session'],
    },
    {
        key: 'narratives',
        icon: 'auto_stories',
        outlined: true,
        labelKey: 'narratives',
        routeName: 'narratives',
        activeRouteNames: ['narratives', 'narrative-detail', 'findings'],
    },
    {
        key: 'export',
        icon: 'file_upload',
        outlined: true,
        labelKey: 'export',
        disabled: true,
    },
];

const isActive = (item: NavItem): boolean => {
    const names = item.activeRouteNames ?? (item.routeName ? [item.routeName] : []);
    return names.includes(route.name as string);
};

// `suppressTooltip` / `suppressAt` live at module scope (top <script> block) so
// they survive the per-page remount of this component. We remember where the
// pointer was at click time and only re-enable once it moves to a different
// position — a route change / remount reflows the layout and can fire a
// `mousemove` at the same coordinates, which must NOT count as movement.
const onMouseMove = (event: MouseEvent) => {
    if (!suppressTooltip.value) return;
    if (suppressAt && event.clientX === suppressAt.x && event.clientY === suppressAt.y) return;
    suppressTooltip.value = false;
    suppressAt = null;
};

const navigateTo = (item: NavItem, event: MouseEvent) => {
    // Blur immediately so the focus-within tooltip drops too, then suppress
    // the hover tooltip until the next real mouse move.
    (event.currentTarget as HTMLElement | null)?.blur();
    suppressTooltip.value = true;
    suppressAt = { x: event.clientX, y: event.clientY };
    if (item.disabled || !item.routeName) return;
    const matterId = matterStore.currentMatter?.RecordId;
    if (!matterId) return;

    // Always land on the section's landing page — even from one of its
    // sub-routes (e.g. Dialogues from a session, Narratives from a narrative or
    // findings). Only skip the push when we're already on the exact landing
    // route so we don't add a redundant history entry.
    if (route.name !== item.routeName) {
        router.push({ name: item.routeName, params: { matterId } });
    }

    // And make sure that section's sidebar is open, whether we just navigated
    // or were already there with it manually collapsed.
    requestSidebarExpand();
};

const itemActive = computed(() => navItems.map(isActive));
</script>

<template>
    <nav class="matter-nav-rail" :aria-label="t('matters.nav.ariaLabel')">
        <div
            v-for="(item, index) in navItems"
            :key="item.key"
            class="matter-nav-rail-item-wrap"
            :class="{ 'matter-nav-rail-item-wrap-tooltip-suppressed': suppressTooltip }"
            @mousemove="onMouseMove"
        >
            <button
                type="button"
                class="matter-nav-rail-item"
                :class="{
                    'matter-nav-rail-item-active': itemActive[index],
                    'matter-nav-rail-item-disabled': item.disabled,
                }"
                :aria-label="
                    item.disabled
                        ? t('matters.nav.comingSoonAria', {
                              title: t(`matters.nav.${item.labelKey}.title`),
                          })
                        : t(`matters.nav.${item.labelKey}.title`)
                "
                :aria-current="itemActive[index] ? 'page' : undefined"
                :disabled="item.disabled"
                @click="navigateTo(item, $event)"
            >
                <span :class="item.outlined ? 'material-icons-outlined' : 'material-icons'">{{
                    item.icon
                }}</span>
            </button>

            <div class="matter-nav-rail-tooltip" role="tooltip">
                <div class="matter-nav-rail-tooltip-title">
                    {{ t(`matters.nav.${item.labelKey}.title`) }}
                </div>
                <div class="matter-nav-rail-tooltip-text">
                    {{ t(`matters.nav.${item.labelKey}.description`)
                    }}<br v-if="item.disabled" />{{ item.disabled ? t('matters.nav.comingSoon') : '' }}
                </div>
            </div>
        </div>
    </nav>
</template>
